<template>
  <mapbox-marker
    :lng-lat="lngLat"
    anchor="center"
    :draggable="draggable"
    @drag="drag"
    @dragend="$emit('dragend', $event)"
    @click="openPopup"
  >
    <template #icon>
      <CheckpointIcon
        :radius="radiusInPixels"
        :start="start"
        :end="end"
        :visited="visited"
        @dblclick.stop.prevent
      />
    </template>
  </mapbox-marker>
  <mapbox-marker
    v-if="checkpoint.name"
    :lng-lat="lngLat"
    :offset="[radiusInPixels * 0.6, radiusInPixels * 0.71]"
    anchor="top-left"
    @click="openPopup"
  >
    <template #icon>
      <span class="name-label" @dblclick.stop.prevent
        >{{ checkpoint.name }}
      </span>
    </template>
  </mapbox-marker>

  <mapbox-popup
    :lng-lat="lngLat"
    :offset="[0, -radiusInPixels - 10]"
    v-if="popup && showPopup"
    anchor="bottom"
    @close="showPopup = false"
  >
    <div v-if="draggable">
      <FormKit type="text" label="Name" v-model="checkpoint.name" />
      <FormKit
        type="textarea"
        label="Description"
        v-model="checkpoint.description"
      />
      <FormKit type="select" label="Radius" v-model.number="checkpoint.radius">
        <CheckpointRadiusOptions />
      </FormKit>
      <button @click="$emit('delete', checkpoint)">Delete</button>
    </div>
    <div v-else>
      <h3>{{ checkpoint.name }}</h3>
      <p>{{ checkpoint.description }}</p>
    </div>
  </mapbox-popup>
</template>
<script setup lang="ts">
import Mapbox from "mapbox-gl";

const DEFAULT_RADIUS = 100;

defineEmits(["delete", "dragend"]);

const props = defineProps<{
  draggable?: boolean;
  popup?: boolean;
  start?: boolean;
  end?: boolean;
  checkpoint: Partial<Row<"checkpoints">>;
  visited?: boolean;
  popupOpen?: boolean;
}>();

const map = useMap();

const lngLat = computed(() => [
  Number(props.checkpoint.lng),
  Number(props.checkpoint.lat),
]);

const showPopup = ref(props.popupOpen);

function openPopup(e: MouseEvent) {
  setTimeout(() => (showPopup.value = true), 10);
}

const metersToPixels = useMetersToPixels();

function drag(e: { target: Mapbox.Marker }) {
  const { lat, lng } = e.target.getLngLat();
  props.checkpoint.lat = lat.toFixed(8);
  props.checkpoint.lng = lng.toFixed(8);
}

const radiusInPixels = computed(() => {
  const pixels = metersToPixels(
    props.checkpoint.radius || DEFAULT_RADIUS,
    props.checkpoint.lat
  );
  return Math.max(6, pixels);
});

const checkPath = computed(() => {
  const factor = radiusInPixels.value / 12;
  return `M${5 * factor},${10 * factor} L${12 * factor},${16 * factor} L${
    24 * factor
  },0`;
});

const viewBox = () =>
  `0 0 ${radiusInPixels.value * 2 + 4} ${radiusInPixels.value * 2 + 4}`;
</script>
<style scoped>
.name-label {
  color: red;
  text-shadow: 0px 0px 3px white;
}

svg.draggable {
  cursor: move;
}

.mapboxgl-marker-anchor-center svg {
  margin-top: 6px;
}
</style>
